import React, { memo, useEffect, useState, ChangeEvent, useCallback } from 'react'
import { DatePicker } from 'components/forms/DatePicker'
import moment from 'moment-timezone'
import { Box, Typography } from 'transfix-ui/core'
import { IFullStop } from 'types/shipment'
import { IStop } from 'apps/fleet-planner/types/loads'
import { TIME_INPUT_FORMAT, TimeInput, buildDateTime } from 'transfix-ui'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'

interface IInOutTimesProps {
  marketplaceStop?: IFullStop
  stop: Partial<IStop>
  onStopChange: (stop: Partial<IStop>) => void
  variant?: string
  timeFormat: TIME_INPUT_FORMAT
  /**
   * @default true
   */
  isCheckinVisible?: boolean
  /**
   * @default true
   */
  isCheckoutVisible?: boolean
}

const getInitialState = (timeStamp: string | null, tz: string, timeFormat: string): string =>
  timeStamp ? moment(timeStamp).tz(tz).format(timeFormat) : ''

export const InOutTimes = memo<IInOutTimesProps>(
  ({
    stop,
    onStopChange,
    variant,
    timeFormat = TIME_INPUT_FORMAT.MILITARY_SHORT,
    marketplaceStop,
    isCheckinVisible = true,
    isCheckoutVisible = true,
  }) => {
    const className = variant === 'load-details' ? 'pl0' : ''
    const { status } = marketplaceStop || {}
    const { arrivedAt = '', leftAt = '' } = status || {}
    const {
      checkedInAt = '',
      checkedOutAt = '',
      displayTimezone,
      startAt,
      stopSequence,
      stopType,
    } = stop || {}
    const initialTZ = displayTimezone || moment.tz.guess()

    const [checkInDate, setCheckInDate] = useState(
      getInitialState(
        arrivedAt || checkedInAt || startAt || null,
        initialTZ,
        FORMATS.flatpickrDateTimeFormat
      )
    )

    const [checkInTime, setCheckInTime] = useState(
      getInitialState(arrivedAt || checkedInAt || startAt || null, initialTZ, timeFormat)
    )

    const [checkOutDate, setCheckOutDate] = useState(
      getInitialState(leftAt || checkedOutAt || null, initialTZ, FORMATS.flatpickrDateTimeFormat)
    )

    const [checkOutTime, setCheckOutTime] = useState(
      getInitialState(leftAt || checkedOutAt || null, initialTZ, timeFormat)
    )

    const onCheckInDateChangeHandler = useCallback((dates: string[]) => {
      if (!dates[0]) {
        setCheckInDate('')
      } else {
        setCheckInDate(moment(dates[0]).format(FORMATS.flatpickrDateTimeFormat))
      }
    }, [])

    const onCheckInTimeChangeHandler = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setCheckInTime(value)
      },
      []
    )

    const onCheckOutDateChangeHandler = useCallback((dates: string[]) => {
      if (!dates[0]) {
        setCheckOutDate('')
      } else {
        setCheckOutDate(moment(dates[0]).format(FORMATS.flatpickrDateTimeFormat))
      }
    }, [])

    const onCheckOutTimeChangeHandler = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setCheckOutTime(value)
      },
      []
    )

    useEffect(() => {
      const checkedInAtTime = buildDateTime(checkInDate, checkInTime, timeFormat, initialTZ)
      onStopChange({
        checkedInAt: checkedInAtTime ? checkedInAtTime.toISOString() : null,
      })
    }, [checkInDate, checkInTime])

    useEffect(() => {
      const checkedOutAtTime = buildDateTime(checkOutDate, checkOutTime, timeFormat, initialTZ)
      if (checkedOutAtTime) {
        onStopChange({
          checkedOutAt: checkedOutAtTime ? checkedOutAtTime.toISOString() : null,
        })
      }
    }, [checkOutDate, checkOutTime])

    return (
      <>
        {isCheckinVisible && (
          <>
            <label className='label' htmlFor='check-in'>
              Check In
            </label>
            <div className='row' data-test={`inline-edit-inout-${stopType}-${stopSequence}`}>
              <div className={`col-xs-6 col-md-4 ${className}`}>
                <DatePicker
                  disabled={!!arrivedAt}
                  date={checkInDate}
                  placeholder={DISPLAY.emptyDateTime}
                  onDateChange={onCheckInDateChangeHandler}
                  dataTest={`stop-check-in-date-${stopType}-${stopSequence}`}
                  id={`stop-check-in-date-${stopType}-${stopSequence}`}
                  large
                />
              </div>
              <Box className='fl' display='flex' alignItems='center'>
                <TimeInput
                  disabled={!!arrivedAt}
                  timeFormat={timeFormat}
                  placeholder={DISPLAY.emptyDateTime}
                  value={checkInTime}
                  onChange={onCheckInTimeChangeHandler}
                  dataTest={`stop-check-in-time-${stopType}-${stopSequence}`}
                  large
                />
                <span className='pl1 f6 lh-copy'>{moment().tz(initialTZ).format('z')}</span>
              </Box>
            </div>
          </>
        )}
        {arrivedAt && isCheckinVisible && (
          <Typography variant='caption'>
            This is the Check-In timestamp Transfix has on file.
          </Typography>
        )}
        {isCheckoutVisible && (
          <div className='mt3'>
            <label className='label' htmlFor='check-in'>
              Check Out
            </label>
            <div className='row'>
              <div className={`col-xs-6 col-md-4 ${className}`}>
                <DatePicker
                  disabled={!!leftAt}
                  date={checkOutDate}
                  placeholder={DISPLAY.emptyDateTime}
                  onDateChange={onCheckOutDateChangeHandler}
                  dataTest={`stop-check-out-date-${stopType}-${stopSequence}`}
                  id={`stop-check-out-date-${stopType}-${stopSequence}`}
                  large
                />
              </div>
              <Box className='fl' display='flex' alignItems='center'>
                <TimeInput
                  disabled={!!leftAt}
                  timeFormat={timeFormat}
                  placeholder={DISPLAY.emptyDateTime}
                  value={checkOutTime}
                  onChange={onCheckOutTimeChangeHandler}
                  dataTest={`stop-check-out-time-${stopType}-${stopSequence}`}
                  large
                />
                <span className='pl1 f6 lh-copy'>{moment().tz(initialTZ).format('z')}</span>
              </Box>
            </div>
          </div>
        )}
        {leftAt && isCheckoutVisible && (
          <Typography variant='caption'>
            This is the Check-Out timestamp Transfix has on file.
          </Typography>
        )}
      </>
    )
  }
)
